import { useState } from 'react';
import { Camera, ExternalLink, FileUp, ShieldAlert } from 'lucide-react';
import { Button, ErrorState, Skeleton, StatusBadge } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { formatDateTime } from '@/lib/dates';
import { CameraCapture } from './CameraCapture';
import {
  uploadHrDoc,
  useHrDocUrl,
  usePrivateDetails,
  usePrivateMutations,
  type PrivateDetails as PrivateDetailsRow,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * ת"ז ופרטי בנק של עובד אחד (מיגרציה 092).
 *
 * אותו רכיב בקונסולה ובאפליקציה: אלה אותם שדות, ואותה פונקציה
 * בשרת מחליטה מי רשאי. שני טפסים היו נפרדים רק עד השינוי הראשון.
 *
 * נטען רק כשהוא מוצג, כי כל טעינה נרשמת ביומן הגישה. זה לא
 * חיסכון בתעבורה — זו המטרה.
 */
export function PrivateDetails({ employeeId }: { employeeId: string }) {
  const details = usePrivateDetails(employeeId, true);

  if (details.isPending) return <Skeleton className="h-48" />;
  if (details.isError) return <ErrorState onRetry={() => void details.refetch()} />;
  if (!details.data) return null;

  // key ולא סנכרון ב effect: כשהערכים מגיעים מהשרת הטופס נטען
  // מחדש איתם, כמו בשאר המסכים כאן
  return (
    <PrivateForm
      key={details.data.updated_at ?? 'new'}
      employeeId={employeeId}
      loaded={details.data}
    />
  );
}

function PrivateForm({
  employeeId,
  loaded,
}: {
  employeeId: string;
  loaded: PrivateDetailsRow;
}) {
  const { save } = usePrivateMutations(employeeId);

  const [nationalId, setNationalId] = useState(loaded.national_id ?? '');
  const [bankNumber, setBankNumber] = useState(loaded.bank_number ?? '');
  const [bankBranch, setBankBranch] = useState(loaded.bank_branch_number ?? '');
  const [bankAccount, setBankAccount] = useState(loaded.bank_account_number ?? '');
  const [docPath, setDocPath] = useState<string | null>(loaded.id_doc_path ?? null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const docUrl = useHrDocUrl(docPath);

  const attach = (file: File | Blob, contentType?: string) => {
    setError(null);
    setSaved(false);
    setBusy(true);
    uploadHrDoc(employeeId, 'id', file, contentType)
      .then((path) => {
        setDocPath(path);
        setIsCameraOpen(false);
      })
      .catch((caught: unknown) => setError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <form
      className="grid gap-4 rounded-md border border-line bg-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setSaved(false);
        save
          .mutateAsync({
            national_id: nationalId,
            id_doc_path: docPath,
            bank_number: bankNumber,
            bank_branch_number: bankBranch,
            bank_account_number: bankAccount,
          })
          .then(() => setSaved(true))
          .catch((caught: unknown) => setError(errorMessage(caught)));
      }}
    >
      <p className="m-0 flex items-start gap-2 text-body-sm text-muted">
        <ShieldAlert size={18} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          מידע רגיש. רואים אותו רק העובד/ת ומי שמחזיק את ההרשאה למסמכים אישיים, וכל צפייה כאן
          נרשמת ביומן.
        </span>
      </p>

      <div className="grid gap-1">
        <label htmlFor="national-id" className="text-label font-medium">
          תעודת זהות
        </label>
        <input
          id="national-id"
          dir="ltr"
          inputMode="numeric"
          value={nationalId}
          onChange={(event) => setNationalId(event.target.value)}
          className={FIELD}
        />
        <p className="m-0 text-body-sm text-muted">תשע ספרות. ספרת הביקורת נבדקת בשמירה.</p>
      </div>

      <div className="grid gap-2">
        <span className="text-label font-medium">צילום תעודת זהות</span>

        {docPath && (
          <p className="m-0 flex flex-wrap items-center gap-2 text-body-sm">
            <StatusBadge tone="good">קובץ קיים</StatusBadge>
            {docUrl.data && (
              <a
                href={docUrl.data}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-ink"
              >
                <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
                פתיחת הקובץ
              </a>
            )}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-sm border border-muted px-3 text-body">
            <FileUp size={18} strokeWidth={1.75} aria-hidden="true" />
            העלאת תמונה או PDF
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) attach(file);
              }}
              className="sr-only"
            />
          </label>

          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setIsCameraOpen((open) => !open)}
          >
            <Camera size={18} strokeWidth={1.75} aria-hidden="true" />
            {isCameraOpen ? 'סגירת המצלמה' : 'צילום במצלמה'}
          </Button>

          {busy && <span className="text-body-sm">מעלה…</span>}
        </div>

        {isCameraOpen && (
          <CameraCapture
            onCapture={(blob) => attach(blob, 'image/jpeg')}
            onCancel={() => setIsCameraOpen(false)}
          />
        )}
      </div>

      <fieldset className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-3">
        <legend className="px-1 text-label font-medium">חשבון בנק</legend>
        <Field label="מספר בנק" value={bankNumber} onChange={setBankNumber} />
        <Field label="מספר סניף" value={bankBranch} onChange={setBankBranch} />
        <Field label="מספר חשבון" value={bankAccount} onChange={setBankAccount} />
      </fieldset>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {saved && <p className="m-0 text-body-sm text-good">נשמר.</p>}
      {loaded.updated_at && (
        <p className="m-0 text-body-sm text-muted">
          עודכן לאחרונה: {formatDateTime(loaded.updated_at)}
        </p>
      )}

      <div>
        <Button type="submit" variant="primary" disabled={busy || save.isPending}>
          שמירת הפרטים
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = `bank-${label}`;
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {label}
      </label>
      <input
        id={id}
        dir="ltr"
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD}
      />
    </div>
  );
}
